// Cómo trata el agente este documento, junto al documento (4.5, D6, D11).
//
// Es lo que sobrevive del Workspace. La sección entera se retiró —tenía mucha
// información y no se entendía— y de ella quedaban dos cosas: corregir el tipo
// y dejar de usar un documento sin borrarlo.
//
// **El cambio de tipo se va el 2026-09-19 (D6).** Los siete tipos dejan de
// decidir nada al responder: un documento entra en lo que el agente sabe a
// través de **una referencia**, no por su tipo. Pedirle al cliente que elija
// entre siete categorías cuyo efecto ya no existe era pedirle una decisión que
// no cambia nada, que es justo por lo que la sección se quitó.
//
// En su sitio va **la referencia**: la frase que el agente sabe de este archivo
// sin abrirlo. Es lo que de verdad decide si lo usa y cómo lo cuenta, y hasta
// hoy no se veía en ninguna parte.
//
// Va dentro del diálogo del documento y **no en la fila**, por dos razones. La
// primera es el criterio de visible/accesible/oculto: a la pregunta «¿se
// entiende sin conocer su contexto?» la respuesta sigue siendo no. La segunda
// es más terca: la fila está escrita **dos veces** en `ContentManager`, una
// para la rejilla y otra para la lista.
//
// Y por eso mismo todo esto es **en línea, sin abrir otro diálogo**: ya estamos
// dentro de uno, y un modal dentro de otro lo prohíbe el estándar.
//
// No reimplementa nada: llama a `workspacesService`, que es la misma puerta que
// usa el Brain.

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field-error'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { log } from '@/lib/logger'
import { workspacesService, type Elemento } from '@/services/workspacesService'

interface Props {
  /** El documento que se está mirando. `null` mientras no haya ninguno. */
  documentoId: number | null
}

export function ComoLoTrataElAgente({ documentoId }: Props) {
  const { t } = useTranslation()

  const [elemento, setElemento] = useState<Elemento | null>(null)
  const [cargando, setCargando] = useState(false)
  const [excluyendo, setExcluyendo] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [faltaMotivo, setFaltaMotivo] = useState(false)
  const [guardando, setGuardando] = useState(false)

  const cargar = useCallback(async () => {
    if (documentoId === null) return
    setCargando(true)
    try {
      const elementos = await workspacesService.elementos()
      setElemento(elementos.find((e) => e.id === documentoId) ?? null)
    } catch (error) {
      // Se calla en pantalla a propósito: esto es un añadido dentro del
      // diálogo de editar un documento, y un error aquí no puede impedir
      // guardar el título. Queda en el registro, que es donde se mira.
      log.error('ComoLoTrataElAgente: no se pudo leer como lo trata', error, {
        component: 'ComoLoTrataElAgente',
      })
      setElemento(null)
    } finally {
      setCargando(false)
    }
  }, [documentoId])

  useEffect(() => {
    setExcluyendo(false)
    setMotivo('')
    setFaltaMotivo(false)
    void cargar()
  }, [cargar])

  // Un documento que el espacio todavía no conoce —recién subido, o de otra
  // cuenta— no tiene nada que enseñar aquí. Callar es mejor que enseñar una
  // sección vacía que no dice qué hacer con ella.
  if (documentoId === null || cargando || !elemento) return null

  const marcar = async (utilizable: boolean, razon?: string) => {
    setGuardando(true)
    try {
      await workspacesService.marcarUtilizable(elemento.id, { utilizable, motivo: razon })
      toast.success(t('knowledge.agente.marked'))
      setExcluyendo(false)
      setMotivo('')
      await cargar()
    } catch (error) {
      log.error('ComoLoTrataElAgente: no se pudo marcar', error, {
        component: 'ComoLoTrataElAgente',
      })
      toast.error(t('knowledge.agente.failed'))
    } finally {
      setGuardando(false)
    }
  }

  return (
    // Sin caja propia. Ya estamos dentro del dialogo del documento, que es el
    // contenedor, y el estandar da dos niveles: meter una tercera aqui es la
    // caja dentro de la caja que la puerta de cajas cuenta.
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium">{t('knowledge.agente.howTreated')}</p>

          {/* La referencia: lo que el agente sabe de este archivo sin abrirlo.
              La frase la escribe el servidor al leerlo. */}
          {elemento.referencia ? (
            <p className="text-xs text-muted-foreground">{elemento.referencia.linea}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {t('knowledge.agente.sin_referencia')}
            </p>
          )}

          {/* Un archivo que espera confirmación no se usa todavía, y decirlo
              aquí evita la pregunta de por qué el agente no lo encuentra. */}
          {elemento.referencia && !elemento.referencia.aceptada && (
            <p className="text-xs text-muted-foreground">
              {t('knowledge.agente.esperando_confirmacion')}
            </p>
          )}

          {!elemento.utilizable && (
            <p className="text-xs text-muted-foreground">{elemento.motivo}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            size="xs"
            variant="ghost"
            onClick={() =>
              elemento.utilizable ? setExcluyendo((v) => !v) : void marcar(true)
            }
            aria-expanded={elemento.utilizable ? excluyendo : undefined}
            disabled={guardando}
          >
            {elemento.utilizable
              ? t('knowledge.agente.markUnusable')
              : t('knowledge.agente.markUsable')}
          </Button>
        </div>
      </div>

      {excluyendo && elemento.utilizable && (
        // Marcar algo como no utilizable **exige motivo** en el servidor:
        // sin él, mañana nadie recuerda por qué ese documento no se usa y
        // acaba volviendo. Volver a usarlo no lo pide, porque deshacer no
        // destruye. Lo de aquí no sustituye a la comprobación del servidor:
        // evita el viaje, y que el error vuelva cuando ya se olvidó por qué.
        <div className="space-y-2">
          <Label id="motivo-no-utilizable-etiqueta" htmlFor="motivo-no-utilizable">
            {t('knowledge.agente.unusableHelp')}
          </Label>
          <Textarea
            id="motivo-no-utilizable"
            // `htmlFor` ya lo asocia para un lector de pantalla, pero el
            // analizador no puede seguir la asociacion a traves de un
            // componente y lo da por sin etiquetar.
            aria-labelledby="motivo-no-utilizable-etiqueta"
            value={motivo}
            rows={2}
            aria-invalid={faltaMotivo}
            aria-describedby={faltaMotivo ? 'motivo-no-utilizable-error' : undefined}
            onChange={(evento) => {
              setMotivo(evento.target.value)
              if (faltaMotivo) setFaltaMotivo(false)
            }}
          />
          {faltaMotivo && (
            <FieldError id="motivo-no-utilizable-error">
              {t('knowledge.agente.unusableNeedsReason')}
            </FieldError>
          )}
          <Button
            size="xs"
            variant="destructive"
            aria-label={t('knowledge.agente.confirmUnusable')}
            disabled={guardando}
            onClick={() => {
              if (!motivo.trim()) {
                setFaltaMotivo(true)
                return
              }
              void marcar(false, motivo.trim())
            }}
          >
            {t('knowledge.agente.confirmUnusable')}
          </Button>
        </div>
      )}
    </div>
  )
}
